import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import './DataTable.scss';
import { Checkbox } from 'pepsico-ds';
import { cn } from '@/helpers/shadcn/utils';
import { useUrlParams } from '@/hooks/useUrlParams';

const NoDataFound = ({ width }) => {
  return (
    <tr>
      <td colSpan={width}>
        <div className='infocard $spacing-p05 dark' role='status'>
          <div className='infocard-content $spacing-p04 $color-text-secondary'>
            <svg
              width='48'
              height='48'
              viewBox='0 0 48 48'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              role='img'>
              <g id='Vector'>
                <path
                  d='M30 9H36L28 1L20 9H26V17.34L30 21.34V9Z'
                  fill='#3A3A3A'></path>
                <path
                  d='M3.62003 0.619141L0.780029 3.43914L14 16.6591V29.0191L8.00003 29.0391L16 36.9991L24 28.9991L18 29.0191V20.6591L37.56 40.2191L40.38 37.3791L3.62003 0.619141Z'
                  fill='currentColor'></path>
              </g>
            </svg>
            <span className='font font-typography $color-text-primary dark $font-heading-h6-bold $color-text-secondary'>
              No data found!
            </span>
            <span className='font font-typography $color-text-primary dark $font-small-regular $color-text-secondary'>
              Please try a different filter value
            </span>
          </div>
        </div>
      </td>
    </tr>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  return (
    <div id='pagination'>
      <nav
        id='table-pagination'
        className='pagination number medium'
        role='tablist'>
        <ul>
          <li className='prev-next'>
            <div className='base-button default dark'>
              <button
                className='base-button-medium default dark $color-border-gray-700 $font-xsmall-regular $spacing-p02'
                data-testid='prev-button'
                data-accessible='true'
                data-index='-1'
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 0}>
                <span
                  className='icon $color-icon dark small material-icons-outlined base-button-icon'
                  role='img'
                  style={{
                    fontSize: '16px',
                    fontVariationSettings:
                      '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48'
                  }}>
                  chevron_left
                </span>
              </button>
            </div>
          </li>

          {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
            let pageNum = index;
            if (currentPage > 2) {
              if (totalPages <= 5) {
                pageNum = index;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 5 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
            }

            return (
              <li key={pageNum}>
                <div
                  className={`base-button default dark ${
                    currentPage === pageNum ? 'active' : ''
                  }`}>
                  <button
                    className='base-button-medium default dark $color-border-gray-700 $font-xsmall-regular $spacing-p02'
                    data-index={pageNum}
                    aria-checked={currentPage === pageNum}
                    role='tab'
                    onClick={() => onPageChange(pageNum)}>
                    <span
                      className={`font font-typography $color-text-primary dark ${
                        currentPage === pageNum
                          ? '$font-small-bold'
                          : '$font-small-regular'
                      } base-button-text ${
                        currentPage === pageNum ? 'active' : ''
                      }`}>
                      {pageNum + 1}
                    </span>
                  </button>
                </div>
              </li>
            );
          })}

          <li className='prev-next'>
            <div className='base-button default dark'>
              <button
                className='base-button-medium default dark $color-border-gray-700 $font-xsmall-regular $spacing-p02'
                data-testid='next-button'
                data-accessible='true'
                data-index='-2'
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}>
                <span
                  className='icon $color-icon dark small material-icons-outlined base-button-icon'
                  role='img'
                  style={{
                    fontSize: '16px',
                    fontVariationSettings:
                      '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48'
                  }}>
                  chevron_right
                </span>
              </button>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
};

const DataTable = ({
  tableHeader,
  subHeader,
  columns,
  isDark = false,
  showColumnFilters = true,
  staticFilters = [],
  selection = 'null',
  downloadFileName = 'download.csv',
  pageSize = 10,
  apiPageSize = 50,
  enableSorting = true,
  onRowClick,
  fetchFunction,
  setTotalStoresCount
}) => {
  const [apiPage, setApiPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentData, setCurrentData] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(currentPage);
  const [previousFiltersJSON, setPreviousFiltersJSON] = useState(null);

  const rowHeightRef = useRef(10);
  const timeoutRef = useRef(null);

  const filterKeys = useMemo(() => columns.map(c => c.columnKey), [columns]);
  const managedKeys = useMemo(() => ['sort', ...filterKeys], [filterKeys]);

  const { params: urlParams, setParams: setUrlParams } =
    useUrlParams(managedKeys);

  const sortConfig = useMemo(() => {
    const sortParam = urlParams.sort;
    if (sortParam) {
      const [key, direction] = sortParam.split(':');
      if (columns.some(col => col.columnKey === key)) {
        return { key, direction };
      }
    }
    return { key: null, direction: null };
  }, [urlParams.sort, columns]);

  const columnFilters = useMemo(() => {
    const filters = {};
    const staticFilterKeys = new Set(staticFilters.map(f => f.columnKey));

    filterKeys.forEach(key => {
      if (urlParams[key] && !staticFilterKeys.has(key)) {
        filters[key] = urlParams[key];
      }
    });

    staticFilters.forEach(filter => {
      filters[filter.columnKey] = filter.value;
    });

    return filters;
  }, [urlParams, filterKeys, staticFilters]);

  const filtersWithSort = useMemo(() => {
    const combined = { ...columnFilters };
    if (sortConfig.key && sortConfig.direction) {
      combined.sort = `${sortConfig.key}:${sortConfig.direction}`;
    }
    return combined;
  }, [columnFilters, sortConfig]);

  const [searchValues, setSearchValues] = useState({});

  const { data, isLoading, error } = fetchFunction(
    apiPage,
    apiPageSize,
    filtersWithSort
  );

  useEffect(() => {
    if (data) {
      setCurrentData(prevData => {
        return apiPage === 0 ? data.items : [...prevData, ...data.items];
      });
      setTotalStoresCount && setTotalStoresCount(data.totalItems);
    }
  }, [data, apiPage, pageSize, setTotalStoresCount]);

  useEffect(() => {
    if (!currentData) return;
    rowHeightRef.current = pageSize * 60;
  }, [currentData, pageSize]);

  useEffect(() => {
    const currentFilters = { ...columnFilters };
    delete currentFilters.sort;
    const currentFiltersJSON = JSON.stringify(currentFilters);

    if (
      previousFiltersJSON !== null &&
      currentFiltersJSON !== previousFiltersJSON
    ) {
      setApiPage(0);
      setCurrentPage(0);
      setCurrentPageIndex(0);
      setCurrentData([]);
    }
    setPreviousFiltersJSON(currentFiltersJSON);
  }, [columnFilters, previousFiltersJSON]);

  const totalPages = useMemo(() => {
    if (!data?.totalItems) return 0;
    return Math.ceil(data.totalItems / pageSize);
  }, [data?.totalItems, pageSize]);

  useEffect(() => {
    const currentFiltersJSON = JSON.stringify(columnFilters);
    if (currentFiltersJSON !== previousFiltersJSON) {
      const initialSearchValues = {};
      const staticFilterKeys = new Set(staticFilters.map(f => f.columnKey));
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (!staticFilterKeys.has(key) && key !== 'sort') {
          initialSearchValues[key] = value || '';
        }
      });
      setSearchValues(initialSearchValues);
    }
  }, [columnFilters, staticFilters, previousFiltersJSON]);

  const paginatedData = useMemo(() => {
    if (!currentData || currentData.length === 0) return [];

    const start = currentPageIndex * pageSize;
    return currentData.slice(start, start + pageSize);
  }, [currentData, currentPageIndex, pageSize]);

  const handleSort = key => {
    if (!enableSorting) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null; // Clear sort if clicking the already descending sorted column
    }

    const newSortValue = direction ? `${key}:${direction}` : null;
    setUrlParams({ sort: newSortValue });

    // Reset pagination to the first page when sorting changes
    setApiPage(0);
    setCurrentPage(0);
    setCurrentPageIndex(0);
    // Removed: Let the fetch hook handle data update
  };

  const handleColumnSearchChange = (columnKey, value) => {
    setUrlParams({ [columnKey]: value || null });
  };

  const handlePageChange = pageIndex => {
    setCurrentPage(pageIndex);
    setCurrentPageIndex(pageIndex);

    const totalItemsNeeded = (pageIndex + 1) * pageSize;
    const currentlyAvailable = currentData?.length || 0;
    const totalAvailable = Number(data?.totalItems) ?? 0;

    if (
      totalItemsNeeded > currentlyAvailable &&
      !isLoading &&
      totalAvailable > currentlyAvailable
    ) {
      const nextApiPage = Math.floor(currentlyAvailable / apiPageSize);
      setApiPage(nextApiPage);
    }
  };

  const handleColumnSearch = (columnKey, value) => {
    setSearchValues(prev => ({ ...prev, [columnKey]: value }));

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleColumnSearchChange(columnKey, value);
    }, 300);
  };

  const getAbsoluteIndex = paginatedIndex => {
    return currentPageIndex * pageSize + paginatedIndex;
  };

  const isRowSelected = absoluteIndex => {
    return selectedIndices.includes(absoluteIndex);
  };

  const onRowSelect = (rowIndex, isBulkOperation = false) => {
    if (isBulkOperation) {
      if (Array.isArray(rowIndex)) {
        setSelectedIndices(rowIndex);
      } else {
        const allPageIndices = Array.from(
          { length: paginatedData.length },
          (_, i) => getAbsoluteIndex(i)
        );
        setSelectedIndices(rowIndex ? allPageIndices : []);
      }
    } else {
      const absoluteIndex = getAbsoluteIndex(rowIndex);

      if (selection === 'single') {
        setSelectedIndices(prev =>
          prev.includes(absoluteIndex) ? [] : [absoluteIndex]
        );
      } else {
        setSelectedIndices(prev => {
          if (prev.includes(absoluteIndex)) {
            return prev.filter(idx => idx !== absoluteIndex);
          } else {
            return [...prev, absoluteIndex];
          }
        });
      }
    }
  };

  const handleDownload = () => {
    const dataToDownload =
      selectedIndices.length > 0
        ? selectedIndices.map(idx => currentData[idx])
        : currentData;

    const headers = columns.map(col => col.header).join(',');
    const rows = dataToDownload
      .map(row => {
        return columns
          .map(col => {
            if (Array.isArray(row[col.columnKey])) {
              return `"${row[col.columnKey].join(', ')}"`;
            }
            const cellValue = row[col.columnKey] ?? '';
            return `"${String(cellValue).replace(/"/g, '""')}"`;
          })
          .join(',');
      })
      .join('\n');

    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', downloadFileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (error) {
    return <div>Error loading data: {error.message}</div>;
  }

  return (
    <div
      data-testid='datatable-v2'
      className={cn('datatable-v2', isDark ? 'dark' : '')}>
      <div data-testid='datatable-header' className='header'>
        <div className='header-title'>
          <span
            className={cn(
              'font font-typography $color-text-primary font-heading-h4-bold',
              isDark ? 'dark' : ''
            )}>
            {tableHeader}
          </span>
          {subHeader && !isLoading && (
            <span
              data-testid='datatable-subheader'
              className={cn(
                'font font-typography $font-small-regular',
                isDark ? 'dark' : ''
              )}>
              {subHeader}
            </span>
          )}
        </div>
      </div>

      <div id='table-toolbar'>
        <div className='toolbar-container'>
          {selectedIndices.length > 0 && (
            <div className='selected-rows-count'>
              {selectedIndices.length} item
              {selectedIndices.length > 1 ? 's ' : ' '}
              selected
            </div>
          )}
          <div className='actions-container'>
            <button
              data-testid='download-button'
              className={cn(
                'button $color-button medium $color-button-secondary $effect-corner-3 $spacing-py02 $spacing-px04',
                isDark ? 'dark' : ''
              )}
              role='button'
              disabled={selectedIndices.length === 0}
              title={
                selectedIndices.length === 0
                  ? 'Please select one or more items from the table to download'
                  : ''
              }
              onClick={handleDownload}>
              <span
                className={cn(
                  'icon $color-icon large material-icons-outlined $spacing-mr03',
                  isDark ? 'dark' : ''
                )}
                role='img'
                style={{
                  fontSize: '20px',
                  fontVariationSettings:
                    '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48'
                }}>
                save_alt
              </span>
              Download
            </button>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'divider divider-horizontal $color-badge-gray-500 header-divider',
          isDark ? 'dark' : ''
        )}
        style={{ height: '2px', width: '100%' }}></div>

      <div className='content'>
        <table className='responsive-table'>
          <thead>
            <tr className='header-row'>
              {selection === 'multiple' && (
                <th
                  className='column-header'
                  style={{ position: 'relative', width: '40px' }}>
                  <div className='datatable-header-separator'>
                    <div className='header-container checkbox-header'>
                      <Checkbox
                        data-testid='select-all-checkbox'
                        indeterminate={
                          selectedIndices?.length > 0 &&
                          selectedIndices?.length !== currentData.length
                            ? 'true'
                            : undefined
                        }
                        checked={
                          currentData?.length > 0 &&
                          selectedIndices &&
                          selectedIndices.length === currentData.length
                        }
                        onUpdate={val => {
                          if (onRowSelect) {
                            if (val) {
                              const allIndices = Array.from(
                                { length: currentData.length },
                                (_, i) => i
                              );
                              onRowSelect(allIndices, true);
                            } else {
                              onRowSelect([], true);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ padding: '30px 8px' }}></div>
                </th>
              )}
              {columns.map((column, colIndex) => (
                <th
                  key={colIndex}
                  colSpan={1}
                  className='column-header'
                  style={{ position: 'relative', width: '150px' }}>
                  <div className='datatable-header-separator'>
                    <div
                      className='header-container'
                      title={enableSorting ? 'Sort descending' : ''}
                      onClick={() =>
                        enableSorting && handleSort(column.columnKey)
                      }>
                      {column.header}
                      {enableSorting &&
                        sortConfig.key === column.columnKey &&
                        sortConfig.direction && (
                          <span
                            className={`icon $color-icon ${
                              isDark ? 'dark' : ''
                            } small material-icons-outlined`}
                            style={{ fontSize: '16px' }}>
                            {sortConfig.direction === 'asc'
                              ? 'arrow_upward'
                              : 'arrow_downward'}
                          </span>
                        )}
                    </div>
                  </div>

                  {showColumnFilters && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '16px 8px',
                        boxShadow:
                          'inset 0px 2px 0px -10px var(--color-border-default), inset 0px -2px 0px -10px var(--color-border-default)'
                      }}>
                      <div
                        className={cn(
                          'text-input $font-body-regular small',
                          isDark ? 'dark' : ''
                        )}
                        role='textinput'>
                        <div className='input-wrapper'>
                          <input
                            id={`text-input-${colIndex}`}
                            type='text'
                            placeholder='Search'
                            className='dt-column-filter-text-input'
                            value={searchValues[column.columnKey] || ''}
                            onChange={e =>
                              handleColumnSearch(
                                column.columnKey,
                                e.target.value
                              )
                            }
                          />
                          {searchValues[column.columnKey] && (
                            <div
                              className={cn(
                                'base-button naked button-icon small clear-button',
                                isDark ? 'dark' : ''
                              )}>
                              <button
                                className={cn(
                                  'base-button-small color-interaction-active naked $color-border-gray-700 $font-xsmall-regular $spacing-p02',
                                  isDark ? 'dark' : ''
                                )}
                                data-testid='clear-button'
                                onClick={e => {
                                  e.stopPropagation();
                                  handleColumnSearchChange(
                                    column.columnKey,
                                    ''
                                  );
                                  setSearchValues(prev => ({
                                    ...prev,
                                    [column.columnKey]: ''
                                  }));
                                }}>
                                <span
                                  className={cn(
                                    'icon $color-icon small material-icons-outlined base-button-icon',
                                    isDark ? 'dark' : ''
                                  )}
                                  role='img'
                                  style={{
                                    fontSize: '16px',
                                    fontVariationSettings:
                                      '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48'
                                  }}>
                                  <span>
                                    <svg
                                      role='img'
                                      name='clear'
                                      width='16'
                                      height='16'
                                      viewBox='0 0 16 16'
                                      fill='none'
                                      xmlns='http://www.w3.org/2000/svg'>
                                      <path
                                        d='M7.99992 1.33334C4.31325 1.33334 1.33325 4.31334 1.33325 8.00001C1.33325 11.6867 4.31325 14.6667 7.99992 14.6667C11.6866 14.6667 14.6666 11.6867 14.6666 8.00001C14.6666 4.31334 11.6866 1.33334 7.99992 1.33334ZM11.3333 10.3933L10.3933 11.3333L7.99992 8.94001L5.60659 11.3333L4.66659 10.3933L7.05992 8.00001L4.66659 5.60668L5.60659 4.66668L7.99992 7.06001L10.3933 4.66668L11.3333 5.60668L8.93992 8.00001L11.3333 10.3933Z'
                                        fill='#3A3A3A'></path>
                                    </svg>
                                  </span>
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className={cn(
                          'icon $color-icon medium material-icons-outlined',
                          isDark ? 'dark' : ''
                        )}
                        role='img'
                        style={{
                          fontSize: '20px',
                          fontVariationSettings:
                            '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48',
                          paddingTop: '2px'
                        }}>
                        filter_list
                      </span>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr style={{ height: rowHeightRef.current + 'px' }}>
                <td colSpan={columns.length + (selection !== 'null' ? 1 : 0)}>
                  <div className='loading-container'>
                    <div className='loading-spinner'></div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, rowIndex) => {
                    const absoluteIndex = getAbsoluteIndex(rowIndex);
                    const isSelected = isRowSelected(absoluteIndex);
                    return (
                      <tr
                        key={rowIndex}
                        className={cn(
                          'selectable-row',
                          isSelected ? 'selected' : ''
                        )}
                        aria-expanded='false'
                        onClick={e => {
                          if (onRowClick) {
                            onRowClick(row, e);
                          }
                          if (selection !== 'null') {
                            onRowSelect(rowIndex, false);
                          }
                        }}
                        style={{
                          cursor:
                            onRowClick || onRowSelect ? 'pointer' : 'default'
                        }}>
                        {selection !== 'null' && (
                          <td
                            className='column-cell'
                            style={{ width: '40px', paddingLeft: '16px' }}>
                            <div className='checkbox dark'>
                              <Checkbox checked={isSelected} readOnly={true} />
                            </div>
                          </td>
                        )}
                        {columns.map((column, colIndex) => (
                          <td
                            key={colIndex}
                            className='column-cell'
                            style={{ width: '150px' }}>
                            {column.cellData
                              ? column.cellData({ row: { original: row } })
                              : row[column.columnKey] || '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <NoDataFound
                    width={columns.length + (selection !== 'null' ? 1 : 0)}
                  />
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className='footer'>
        <>
          {!isLoading && totalPages > 1 && (
            <Pagination
              currentPage={currentPageIndex}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
          {currentData?.length > 0 && !isLoading && (
            <div className='absolute bottom-6 right-6 z-[1] text-white text-sm'>
              {data?.totalItems} total items
            </div>
          )}
        </>
      </div>
    </div>
  );
};

DataTable.propTypes = {
  selection: PropTypes.oneOf(['null', 'single', 'multiple']).isRequired,
  fetchFunction: PropTypes.func.isRequired,
  downloadFileName: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      columnKey: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      cellData: PropTypes.func
    })
  ).isRequired,
  staticFilters: PropTypes.arrayOf(
    PropTypes.shape({
      columnKey: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  tableHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subHeader: PropTypes.string,
  isDark: PropTypes.bool,
  showColumnFilters: PropTypes.bool,
  onSettingsClicked: PropTypes.func,
  pageSize: PropTypes.number,
  apiPageSize: PropTypes.number,
  onGlobalSearchChange: PropTypes.func,
  enableSorting: PropTypes.bool,
  onRowClick: PropTypes.func,
  setTotalStoresCount: PropTypes.func
};

export default DataTable;
